import { LoaderMessage } from "../../components/loaderMessage/loaderMessage";
import CreateNewTicketModal from "./components/createNewTicketModal";
import SupportContent from "./components/supportContent";
import SupportSidebar from "./components/supportSidebar";
import { useAddSupportTicketComment } from "./hooks/useAddSupportTicketComment";
import { useCreateSupportTicket } from "./hooks/useCreateSupportTicket";
import { useSupportTicketDetails } from "./hooks/useSupportTicketDetails";
import { useSupportTickets } from "./hooks/useSupportTickets";
import { useUpdateSupportTicketStatus } from "./hooks/useUpdateSupportTicketStatus";

/**
 * Support tickets page shell with sidebar, detail content, and create-ticket modal.
 *
 * @returns Full-height support tickets layout.
 */
const SupportTicketLayout = () => {
  const {
    tickets,
    selectedTicketId,
    loading,
    searching,
    error,
    isArchiveView,
    setSelectedTicketId,
    searchTickets,
    refetchTickets,
    toggleArchiveView,
  } = useSupportTickets();

  const { ticketDetail, loadingDetail, detailError, refetchTicketDetail } =
    useSupportTicketDetails(selectedTicketId);

  const { createTicket, isCreating } = useCreateSupportTicket({
    onSuccess: (ticketId) => {
      void refetchTickets({ silent: true, selectTicketId: ticketId });
    },
  });

  const { addComment, isAddingComment } = useAddSupportTicketComment({
    ticketId: selectedTicketId,
    onSuccess: async () => {
      await refetchTicketDetail({ silent: true });
      void refetchTickets({ silent: true, selectTicketId: selectedTicketId });
    },
  });

  const { updateStatus, archiveTicket, isUpdatingStatus, isArchiving } =
    useUpdateSupportTicketStatus({
      ticketId: selectedTicketId,
      onSuccess: async () => {
        await refetchTicketDetail({ silent: true });
        void refetchTickets({ silent: true, selectTicketId: selectedTicketId });
      },
      onArchiveSuccess: () => {
        window.location.reload();
      },
    });

  if (loading) {
    return (
      <div className="flex h-full min-h-0 w-full flex-col overflow-hidden">
        <LoaderMessage message="Loading support tickets..." />
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 w-full flex-col overflow-hidden">
      <div className="flex min-h-0 w-full flex-col justify-center overflow-hidden px-4 pt-4 font-bold">
        Support
      </div>
      <div className="support-tickets-layout flex min-h-0 flex-1 overflow-hidden">
        <div className="support-tickets-sidebar-column min-h-0 w-1/4 min-w-0 overflow-hidden">
          <SupportSidebar
            tickets={tickets}
            selectedTicketId={selectedTicketId}
            searching={searching}
            error={error}
            onSearchTickets={searchTickets}
            onSelectTicket={setSelectedTicketId}
          />
        </div>
        <div className="support-tickets-content-column min-h-0 w-3/4 min-w-0 overflow-hidden">
          <SupportContent
            ticket={ticketDetail}
            loadingDetail={loadingDetail}
            searching={searching}
            listError={error}
            detailError={detailError}
            hasSelectedTicket={Boolean(selectedTicketId)}
            isArchiveActive={isArchiveView}
            onArchiveToggle={toggleArchiveView}
            onAddComment={addComment}
            isAddingComment={isAddingComment}
            onUpdateStatus={updateStatus}
            onArchiveTicket={archiveTicket}
            isUpdatingStatus={isUpdatingStatus}
            isArchiving={isArchiving}
          />
        </div>
      </div>

      <CreateNewTicketModal onCreate={createTicket} isCreating={isCreating} />
    </div>
  );
};

export default SupportTicketLayout;
